import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import tileTimeLimitationApi from '../api/tileTimeLimitationApi';
import { minuteOfDayToTimeString, timeStringToMinuteOfDay } from '../utils/timeOfDay';

const DEFAULT_DENIED_MESSAGE = 'Access to this location is not allowed at this time.';

const DEFAULT_SETTINGS = Object.freeze({
  isEnabled: false,
  weekdayBlockedAllDay: false,
  weekendBlockedAllDay: false,
  weekdayStartMinute: 9 * 60,
  weekdayEndMinute: 17 * 60,
  weekendStartMinute: 9 * 60,
  weekendEndMinute: 17 * 60,
  deniedMessage: '',
  weekdayAllowAllDay: false,
  weekendAllowAllDay: false,
  accessPoints: [],
});

const toAccessPointLabel = (accessPoint) => {
  const gate = String(accessPoint && accessPoint.gate ? accessPoint.gate : '').trim();
  const tile = String(accessPoint && accessPoint.tile ? accessPoint.tile : '').trim();

  if (gate && tile) return `${gate} (${tile})`;
  if (gate) return gate;
  if (tile) return tile;
  return `Access point ${accessPoint && accessPoint.accessPointId ? accessPoint.accessPointId : ''}`.trim();
};

const normalizeSettingsResponse = (response) => {
  const accessPoints = Array.isArray(response && response.accessPoints)
    ? response.accessPoints.map((accessPoint) => ({
        accessPointId: Number(accessPoint.accessPointId),
        gate: accessPoint.gate,
        tile: accessPoint.tile,
        selected: Boolean(accessPoint.selected),
      })).filter((accessPoint) => Number.isFinite(accessPoint.accessPointId))
    : [];

  const weekdayBlockedAllDay = Boolean(response && response.weekdayBlockedAllDay);
  const weekendBlockedAllDay = Boolean(response && response.weekendBlockedAllDay);
  const weekdayStartMinute = Number(response && response.weekdayStartMinute);
  const weekdayEndMinute = Number(response && response.weekdayEndMinute);
  const weekendStartMinute = Number(response && response.weekendStartMinute);
  const weekendEndMinute = Number(response && response.weekendEndMinute);

  return {
    isEnabled: Boolean(response && response.isEnabled),
    weekdayBlockedAllDay,
    weekendBlockedAllDay,
    weekdayStartMinute,
    weekdayEndMinute,
    weekendStartMinute,
    weekendEndMinute,
    deniedMessage: response && response.deniedMessage ? String(response.deniedMessage) : '',
    weekdayAllowAllDay: !weekdayBlockedAllDay && weekdayStartMinute === 0 && weekdayEndMinute === 1439,
    weekendAllowAllDay: !weekendBlockedAllDay && weekendStartMinute === 0 && weekendEndMinute === 1439,
    accessPoints,
  };
};

const getErrorDetail = (error, fallbackMessage) => {
  return (
    (error && error.response && error.response.data) ||
    (error && error.message) ||
    fallbackMessage
  );
};

const LocationSettingsDialog = ({
  visible,
  onHide,
  location,
  customerId,
  appProviderId,
  onSaved,
  onUnauthorized,
}) => {
  const msgs = useRef(null);
  const toast = useRef(null);
  const closeAfterSaveTimeoutRef = useRef(null);
  const weekdayCustomRangeRef = useRef(null);
  const weekendCustomRangeRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [selectedAccessPointIds, setSelectedAccessPointIds] = useState(new Set());
  const [timeError, setTimeError] = useState(null);

  const hasAccessPoints = settings.accessPoints.length > 0;
  const weekdayMode = !settings.isEnabled
    ? 'allow'
    : settings.weekdayBlockedAllDay
      ? 'block'
      : settings.weekdayAllowAllDay
        ? 'allow'
        : 'custom';
  const weekendMode = !settings.isEnabled
    ? 'allow'
    : settings.weekendBlockedAllDay
      ? 'block'
      : settings.weekendAllowAllDay
        ? 'allow'
        : 'custom';
  const weekdayTimeInputsDisabled = saving || weekdayMode !== 'custom';
  const weekendTimeInputsDisabled = saving || weekendMode !== 'custom';
  const deniedMessagePreview = String(settings.deniedMessage || '').trim() || DEFAULT_DENIED_MESSAGE;

  const selectedCount = selectedAccessPointIds.size;

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  const locationName = String(location && location.name ? location.name : '').trim();

  const loadSettings = async () => {
    if (!customerId || !location || !location.id) return;

    try {
      setLoading(true);
      setTimeError(null);
      if (msgs.current) msgs.current.clear();

      const response = await tileTimeLimitationApi.getTileTimeLimitation(customerId, location.id, appProviderId);
      const normalized = normalizeSettingsResponse(response);

      weekdayCustomRangeRef.current = {
        startMinute: normalized.weekdayStartMinute,
        endMinute: normalized.weekdayEndMinute,
      };
      weekendCustomRangeRef.current = {
        startMinute: normalized.weekendStartMinute,
        endMinute: normalized.weekendEndMinute,
      };

      // Legacy backend state can have isEnabled=false with stale minutes. Present disabled state as allow-all-day.
      setSettings(
        normalized.isEnabled
          ? normalized
          : {
              ...normalized,
              weekdayBlockedAllDay: false,
              weekendBlockedAllDay: false,
              weekdayAllowAllDay: true,
              weekendAllowAllDay: true,
              weekdayStartMinute: 0,
              weekdayEndMinute: 1439,
              weekendStartMinute: 0,
              weekendEndMinute: 1439,
            }
      );
      setSelectedAccessPointIds(
        new Set(normalized.accessPoints.filter((accessPoint) => accessPoint.selected).map((accessPoint) => accessPoint.accessPointId))
      );
    } catch (error) {
      if (error && error.response && error.response.status === 401 && onUnauthorized) {
        onUnauthorized(error);
        return;
      }

      const detail = getErrorDetail(error, 'Failed to load location settings.');
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail, life: 5000 });
      }
      if (msgs.current) {
        msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) return;

    loadSettings();
  }, [visible, customerId, location && location.id, appProviderId]);

  useEffect(() => {
    return () => {
      if (closeAfterSaveTimeoutRef.current) {
        window.clearTimeout(closeAfterSaveTimeoutRef.current);
      }
    };
  }, []);

  const accessPointRows = useMemo(() => {
    return [...settings.accessPoints].sort((a, b) => toAccessPointLabel(a).localeCompare(toAccessPointLabel(b)));
  }, [settings.accessPoints]);

  const storeCustomRange = (dayGroup, startMinute, endMinute) => {
    const targetRef = dayGroup === 'weekday' ? weekdayCustomRangeRef : weekendCustomRangeRef;
    targetRef.current = { startMinute, endMinute };
  };

  const restoreCustomRange = (dayGroup, currentStartMinute, currentEndMinute) => {
    const targetRef = dayGroup === 'weekday' ? weekdayCustomRangeRef : weekendCustomRangeRef;
    return targetRef.current || { startMinute: currentStartMinute, endMinute: currentEndMinute };
  };

  const setMinuteField = (dayGroup, fieldName, value, label) => {
    const minutes = timeStringToMinuteOfDay(value);
    if (minutes === null) {
      setTimeError(`Please enter a valid ${label} time (HH:mm).`);
      return;
    }

    setTimeError(null);
    setSettings((prev) => {
      const next = { ...prev, [fieldName]: minutes };
      if (dayGroup === 'weekday' && !next.weekdayAllowAllDay && !next.weekdayBlockedAllDay) {
        storeCustomRange('weekday', next.weekdayStartMinute, next.weekdayEndMinute);
      }
      if (dayGroup === 'weekend' && !next.weekendAllowAllDay && !next.weekendBlockedAllDay) {
        storeCustomRange('weekend', next.weekendStartMinute, next.weekendEndMinute);
      }
      return next;
    });
  };

  const setDayMode = (dayGroup, mode) => {
    setSettings((prev) => {
      const next = { ...prev };

      if (dayGroup === 'weekday') {
        const wasCustom = !prev.weekdayBlockedAllDay && !prev.weekdayAllowAllDay;
        if (wasCustom) {
          storeCustomRange('weekday', prev.weekdayStartMinute, prev.weekdayEndMinute);
        }

        if (mode === 'allow') {
          next.weekdayBlockedAllDay = false;
          next.weekdayAllowAllDay = true;
          next.weekdayStartMinute = 0;
          next.weekdayEndMinute = 1439;
        } else if (mode === 'block') {
          next.weekdayBlockedAllDay = true;
          next.weekdayAllowAllDay = false;
        } else {
          const restoredRange = restoreCustomRange('weekday', prev.weekdayStartMinute, prev.weekdayEndMinute);
          next.weekdayBlockedAllDay = false;
          next.weekdayAllowAllDay = false;
          next.weekdayStartMinute = restoredRange.startMinute;
          next.weekdayEndMinute = restoredRange.endMinute;
        }
      } else {
        const wasCustom = !prev.weekendBlockedAllDay && !prev.weekendAllowAllDay;
        if (wasCustom) {
          storeCustomRange('weekend', prev.weekendStartMinute, prev.weekendEndMinute);
        }

        if (mode === 'allow') {
          next.weekendBlockedAllDay = false;
          next.weekendAllowAllDay = true;
          next.weekendStartMinute = 0;
          next.weekendEndMinute = 1439;
        } else if (mode === 'block') {
          next.weekendBlockedAllDay = true;
          next.weekendAllowAllDay = false;
        } else {
          const restoredRange = restoreCustomRange('weekend', prev.weekendStartMinute, prev.weekendEndMinute);
          next.weekendBlockedAllDay = false;
          next.weekendAllowAllDay = false;
          next.weekendStartMinute = restoredRange.startMinute;
          next.weekendEndMinute = restoredRange.endMinute;
        }
      }

      const weekdayIsAllow = next.weekdayAllowAllDay && !next.weekdayBlockedAllDay;
      const weekendIsAllow = next.weekendAllowAllDay && !next.weekendBlockedAllDay;
      next.isEnabled = !(weekdayIsAllow && weekendIsAllow);

      return next;
    });
  };

  const toggleAccessPoint = (accessPointId, checked) => {
    setSelectedAccessPointIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(accessPointId);
      else next.delete(accessPointId);
      return next;
    });
  };

  const selectAllAccessPoints = () => {
    setSelectedAccessPointIds(new Set(accessPointRows.map((accessPoint) => accessPoint.accessPointId)));
  };

  const clearAllAccessPoints = () => {
    setSelectedAccessPointIds(new Set());
  };

  const validateMinutes = () => {
    const minuteFields = [
      ['weekdayStartMinute', settings.weekdayStartMinute, 'Weekday start'],
      ['weekdayEndMinute', settings.weekdayEndMinute, 'Weekday end'],
      ['weekendStartMinute', settings.weekendStartMinute, 'Weekend start'],
      ['weekendEndMinute', settings.weekendEndMinute, 'Weekend end'],
    ];

    for (const [, value, label] of minuteFields) {
      if (!Number.isInteger(value) || value < 0 || value > 1439) {
        return `${label} time must be between 00:00 and 23:59.`;
      }
    }

    return null;
  };

  const saveSettings = async () => {
    const validationError = validateMinutes();
    setTimeError(validationError);

    if (validationError) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: validationError, life: 5000 });
      }
      return;
    }

    try {
      setSaving(true);
      if (msgs.current) msgs.current.clear();

      const payload = {
        isEnabled: Boolean(settings.isEnabled),
        weekdayBlockedAllDay: Boolean(settings.weekdayBlockedAllDay),
        weekendBlockedAllDay: Boolean(settings.weekendBlockedAllDay),
        deniedMessage: settings.deniedMessage,
        weekdayStartMinute: settings.weekdayStartMinute,
        weekdayEndMinute: settings.weekdayEndMinute,
        weekendStartMinute: settings.weekendStartMinute,
        weekendEndMinute: settings.weekendEndMinute,
        accessPointIds: Array.from(selectedAccessPointIds.values()).map((accessPointId) => Number(accessPointId)),
      };

      await tileTimeLimitationApi.saveTileTimeLimitation(customerId, location.id, payload, appProviderId);

      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: 'Location settings saved.', life: 4000 });
      }

      if (onSaved) {
        if (closeAfterSaveTimeoutRef.current) {
          window.clearTimeout(closeAfterSaveTimeoutRef.current);
        }

        closeAfterSaveTimeoutRef.current = window.setTimeout(() => {
          onSaved({
            ...settings,
            accessPointIds: payload.accessPointIds,
          });
        }, 1200);
      }
    } catch (error) {
      if (error && error.response && error.response.status === 401 && onUnauthorized) {
        onUnauthorized(error);
        return;
      }

      const detail = getErrorDetail(error, 'Failed to save location settings.');
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail, life: 5000 });
      }
      if (msgs.current) {
        msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
      }
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-content-center mb-2">
      <Button label="Close" className="p-button-text" onClick={onHide} disabled={saving} />
      <Button label="Save" icon="pi pi-check" className="p-button-success ml-3" onClick={saveSettings} disabled={loading || saving} />
    </div>
  );

  return (
    <Dialog
      header={locationName ? `Location settings - ${locationName}` : 'Location settings'}
      visible={visible}
      onHide={onHide}
      style={{ width: '70rem', maxWidth: '96vw' }}
      modal
      footer={footer}
    >
      <Messages ref={msgs} />
      <Toast ref={toast} position="top-center" />
      {loading && progressbar}

      {!loading && (
        <Row>
          <Col lg={6} className="mb-3">
            <Card className="mb-3" title="Time Limitation">
              <div className="">
                <small>Times are in British time (Europe/London).</small>
                <div className="mt-2">
                  <small>Choose allow all day, block all day, or set a custom time window to allow Smart Tile scans.</small>
                </div>
              </div>

              <Row>
                <Col md={6} className="mb-3">
                  <Card title="Weekdays">
                    <div className="flex align-items-center flex-wrap gap-3 mb-3">
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="weekday-mode-allow"
                          name="weekday-mode"
                          value="allow"
                          onChange={() => setDayMode('weekday', 'allow')}
                          checked={weekdayMode === 'allow'}
                          disabled={saving}
                        />
                        <label htmlFor="weekday-mode-allow" className="ml-2 mb-0">Allow all day</label>
                      </div>
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="weekday-mode-block"
                          name="weekday-mode"
                          value="block"
                          onChange={() => setDayMode('weekday', 'block')}
                          checked={weekdayMode === 'block'}
                          disabled={saving}
                        />
                        <label htmlFor="weekday-mode-block" className="ml-2 mb-0">Block all day</label>
                      </div>
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="weekday-mode-custom"
                          name="weekday-mode"
                          value="custom"
                          onChange={() => setDayMode('weekday', 'custom')}
                          checked={weekdayMode === 'custom'}
                          disabled={saving}
                        />
                        <label htmlFor="weekday-mode-custom" className="ml-2 mb-0">Set custom scan allowance times</label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="mb-2" style={{ display: 'block' }}>Start</label>
                      <InputText
                        type="time"
                        value={minuteOfDayToTimeString(settings.weekdayStartMinute)}
                        onChange={(e) => setMinuteField('weekday', 'weekdayStartMinute', e.target.value, 'weekday start')}
                        disabled={weekdayTimeInputsDisabled}
                      />
                    </div>
                    <div>
                      <label className="mb-2" style={{ display: 'block' }}>End</label>
                      <InputText
                        type="time"
                        value={minuteOfDayToTimeString(settings.weekdayEndMinute)}
                        onChange={(e) => setMinuteField('weekday', 'weekdayEndMinute', e.target.value, 'weekday end')}
                        disabled={weekdayTimeInputsDisabled}
                      />
                    </div>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card title="Weekends">
                    <div className="flex align-items-center flex-wrap gap-3 mb-3">
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="weekend-mode-allow"
                          name="weekend-mode"
                          value="allow"
                          onChange={() => setDayMode('weekend', 'allow')}
                          checked={weekendMode === 'allow'}
                          disabled={saving}
                        />
                        <label htmlFor="weekend-mode-allow" className="ml-2 mb-0">Allow all day</label>
                      </div>
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="weekend-mode-block"
                          name="weekend-mode"
                          value="block"
                          onChange={() => setDayMode('weekend', 'block')}
                          checked={weekendMode === 'block'}
                          disabled={saving}
                        />
                        <label htmlFor="weekend-mode-block" className="ml-2 mb-0">Block all day</label>
                      </div>
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="weekend-mode-custom"
                          name="weekend-mode"
                          value="custom"
                          onChange={() => setDayMode('weekend', 'custom')}
                          checked={weekendMode === 'custom'}
                          disabled={saving}
                        />
                        <label htmlFor="weekend-mode-custom" className="ml-2 mb-0">Set custom scan allowance times</label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="mb-2" style={{ display: 'block' }}>Start</label>
                      <InputText
                        type="time"
                        value={minuteOfDayToTimeString(settings.weekendStartMinute)}
                        onChange={(e) => setMinuteField('weekend', 'weekendStartMinute', e.target.value, 'weekend start')}
                        disabled={weekendTimeInputsDisabled}
                      />
                    </div>
                    <div>
                      <label className="mb-2" style={{ display: 'block' }}>End</label>
                      <InputText
                        type="time"
                        value={minuteOfDayToTimeString(settings.weekendEndMinute)}
                        onChange={(e) => setMinuteField('weekend', 'weekendEndMinute', e.target.value, 'weekend end')}
                        disabled={weekendTimeInputsDisabled}
                      />
                    </div>
                  </Card>
                </Col>
              </Row>

              {timeError && <small className="p-error">{timeError}</small>}
              <div className="mt-2">
                <small className="text-muted">Allow scans at overnight is possible by setting a later time at start than at end.</small>
              </div>
            </Card>
          </Col>

          <Col lg={6} className="mb-3">
            <Card className="mb-3" title="Denied message">
              <div className="mb-3">
                <label htmlFor="denied-message" className="mb-2" style={{ display: 'block' }}>Denied message</label>
                <InputTextarea
                  id="denied-message"
                  value={settings.deniedMessage}
                  onChange={(e) => setSettings((prev) => ({ ...prev, deniedMessage: e.target.value }))}
                  placeholder={DEFAULT_DENIED_MESSAGE}
                  rows={4}
                  autoResize
                  disabled={saving}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <strong>Preview</strong>
                <div className="mt-2 text-muted">{deniedMessagePreview}</div>
              </div>
            </Card>

            <Card title="Affected access points" subTitle={`Selected ${selectedCount} of ${settings.accessPoints.length} access points.`}>
              <div className="flex align-items-center flex-wrap gap-2 mb-3">
                <Button label="Select all" className="p-button-text" onClick={selectAllAccessPoints} disabled={!hasAccessPoints || saving} />
                <Button label="Clear all" className="p-button-text" onClick={clearAllAccessPoints} disabled={selectedCount === 0 || saving} />
              </div>

              {!hasAccessPoints && (
                <div className="text-muted">No access points are available for this location.</div>
              )}

              {hasAccessPoints && (
                <div style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                  {accessPointRows.map((accessPoint) => (
                    <div key={accessPoint.accessPointId} className="flex align-items-center mb-3">
                      <Checkbox
                        inputId={`location-setting-ap-${accessPoint.accessPointId}`}
                        checked={selectedAccessPointIds.has(accessPoint.accessPointId)}
                        onChange={(e) => toggleAccessPoint(accessPoint.accessPointId, Boolean(e.checked))}
                        disabled={saving}
                      />
                      <label htmlFor={`location-setting-ap-${accessPoint.accessPointId}`} className="ml-2 mb-0">
                        {toAccessPointLabel(accessPoint)}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}
    </Dialog>
  );
};

export default LocationSettingsDialog;